// frontend/src/lib/doorDraft.ts
import { ContactMethod, ResultStatus, VisitActionFlag } from '../types/script'
import type {
  PromptResponseSubmission, RecordVisitRequest, ResolvedScriptDto, SurveyResponseSubmission,
  TagAssignmentSubmission, TurfContactDto,
} from '../types/canvasser'
import { contactWalkState } from './walkStops'
import { DOOR_FOLLOW_UPS } from './outcomeSettings'
import { uuid } from './uuid'

/**
 * [Spec §4.4-§4.9, §6.4] One door's work as plain data. Everything the canvasser records at a door
 * lands here first, then goes to the server as ONE batch with one doorVisitId. Ids are minted once
 * (door id at creation, a person's client id when their entry is first set) and survive a reload via
 * sessionStorage, so a retry or a resumed door is idempotent on the server.
 */

export type CouldntKnockReason = 'Gate' | 'Dog' | 'Unsafe' | 'Vacant' | 'Other'
export const COULDNT_KNOCK_REASONS: { value: CouldntKnockReason; label: string }[] = [
  { value: 'Gate', label: 'Gate or locked' },
  { value: 'Dog', label: 'Dog' },
  { value: 'Unsafe', label: 'Unsafe' },
  { value: 'Vacant', label: 'Vacant' },
  { value: 'Other', label: 'Other' },
]

export type EndChoice = 'finished' | 'declined' | 'busy' | 'language'
export interface FollowUps {
  leftLiterature: boolean
  comeBackLater: boolean
  scheduleCallback: boolean
  dnc: boolean
  language: string
}
/** [Spec §5.2] The follow-ups (and standing instruction) each end choice offers. */
export const FOLLOW_UPS_FOR: Record<EndChoice, (keyof FollowUps)[]> = {
  finished: ['leftLiterature', 'scheduleCallback', 'dnc'],
  declined: ['leftLiterature', 'dnc'],
  busy: ['comeBackLater', 'scheduleCallback'],
  language: ['leftLiterature', 'comeBackLater', 'language'],
}

export interface DraftOutcome { result: ResultStatus; flags: VisitActionFlag[]; note: string | null; dnc: boolean }
export interface ScriptAnswers {
  scriptId: string | null
  surveyResponses: SurveyResponseSubmission[]
  tagAssignments: TagAssignmentSubmission[]
  promptResponses: PromptResponseSubmission[]
}
export interface OffListInfo {
  kind: 'ExistingContact' | 'NewContact'
  memberAnswer: 'Yes' | 'No' | 'NotSure' | null
  phone: string | null
  email: string | null
  context: string | null
}
export interface FlipHome { turfMemberId: string; movedFromLabel: string }
export interface DraftEntry {
  contactId: string
  name: string
  clientVisitId: string
  /** When the outcome was recorded on the phone: becomes clientTimestamp (P3-7 visit date). */
  recordedAt: string
  outcome: DraftOutcome
  answers: ScriptAnswers | null
  offList: OffListInfo | null
  flipHome: FlipHome | null
}
export interface DoorDraft { turfId: string; stopKey: string; doorVisitId: string; entries: DraftEntry[] }
export interface FlipRequest {
  turfMemberId: string; turfId: string; contactId: string; afterClientVisitId: string; movedFromLabel: string
}

const plain = (result: ResultStatus, flags: VisitActionFlag[] = [], note: string | null = null): DraftOutcome =>
  ({ result, flags, note, dnc: false })

export const outcomes = {
  notHome: (leftLiterature: boolean) =>
    plain(ResultStatus.NoAnswer, [VisitActionFlag.ComeBackLater, ...(leftLiterature ? [VisitActionFlag.LeftLiterature] : [])]),
  /** [R27] At a worksite stop the note reads "Couldn't get in: ..."; household doors keep "Couldn't knock: ...". */
  couldntKnock: (reason: CouldntKnockReason, other = '', atWorksite = false) => {
    const label = COULDNT_KNOCK_REASONS.find(r => r.value === reason)!.label
    const extra = reason === 'Other' && other.trim() ? ` — ${other.trim()}` : ''
    return plain(ResultStatus.Inaccessible, [], `${atWorksite ? "Couldn't get in" : "Couldn't knock"}: ${label}${extra}`)
  },
  moved: () => plain(ResultStatus.Moved),
  deceased: () => plain(ResultStatus.Deceased),
  refused: () => plain(ResultStatus.Refused),
  doesntLiveHere: () => plain(ResultStatus.Moved, [VisitActionFlag.NotAtThisAddress], 'Not known at this address'),
  doesntWorkHere: () => plain(ResultStatus.Moved, [VisitActionFlag.NotAtThisWorksite], "Doesn't work here"),
  notAtWork: () => plain(ResultStatus.NoAnswer, [VisitActionFlag.ComeBackLater, VisitActionFlag.NotAtWork], 'Not at work today'),
  fromEnd: (choice: EndChoice, f: FollowUps): DraftOutcome => {
    const offered = new Set(FOLLOW_UPS_FOR[choice])
    const result = { finished: ResultStatus.Completed, declined: ResultStatus.Refused,
      busy: ResultStatus.Busy, language: ResultStatus.LanguageBarrier }[choice]
    const flags: VisitActionFlag[] = []
    if (offered.has('leftLiterature') && f.leftLiterature) flags.push(VisitActionFlag.LeftLiterature)
    if (offered.has('comeBackLater') && f.comeBackLater) flags.push(VisitActionFlag.ComeBackLater)
    if (offered.has('scheduleCallback') && f.scheduleCallback) flags.push(VisitActionFlag.ScheduleCallback)
    const note = offered.has('language') && f.language.trim() ? `Language spoken: ${f.language.trim()}` : null
    return { result, flags, note, dnc: offered.has('dnc') && f.dnc }
  },
}

export function defaultFollowUps(choice: EndChoice): FollowUps {
  return { leftLiterature: false, comeBackLater: choice === 'busy' || choice === 'language',
    scheduleCallback: false, dnc: false, language: '' }
}


export function newDraft(turfId: string, stopKey: string, mint: () => string = uuid): DoorDraft {
  return { turfId, stopKey, doorVisitId: mint(), entries: [] }
}

/** Sets (or replaces) one person's entry. A replaced entry keeps its clientVisitId. */
export function setEntry(
  d: DoorDraft,
  e: Omit<DraftEntry, 'clientVisitId' | 'recordedAt'>,
  now: Date = new Date(),
  mint: () => string = uuid,
): DoorDraft {
  const existing = d.entries.find(x => x.contactId === e.contactId)
  const next: DraftEntry = { ...e, clientVisitId: existing?.clientVisitId ?? mint(), recordedAt: now.toISOString() }
  return existing
    ? { ...d, entries: d.entries.map(x => (x.contactId === e.contactId ? next : x)) }
    : { ...d, entries: [...d.entries, next] }
}

export function handledIds(d: DoorDraft): Set<string> {
  return new Set(d.entries.map(e => e.contactId))
}

const nonEmpty = <T,>(xs: T[] | undefined | null) => (xs && xs.length > 0 ? xs : null)

export function toVisitRequests(d: DoorDraft, campaignId: string): RecordVisitRequest[] {
  return d.entries.map(e => ({
    turfId: d.turfId,
    contactId: e.contactId,
    campaignId,
    scriptId: e.answers?.scriptId ?? null,
    resultStatus: e.outcome.result,
    contactMethod: ContactMethod.Door,
    surveyResponses: nonEmpty(e.answers?.surveyResponses),
    tagAssignments: nonEmpty(e.answers?.tagAssignments),
    promptResponses: nonEmpty(e.answers?.promptResponses),
    actionFlags: nonEmpty(e.outcome.flags),
    clientTimestamp: e.recordedAt,
    clientVisitId: e.clientVisitId,
    doorVisitId: d.doorVisitId,
    visitNote: e.outcome.note,
    markDoNotContact: e.outcome.dnc,
    offListKind: e.offList?.kind ?? null,
    offListMemberAnswer: e.offList?.memberAnswer ?? null,
    offListPhone: e.offList?.phone ?? null,
    offListEmail: e.offList?.email ?? null,
    offListContext: e.offList?.context ?? null,
  }))
}

export function flipRequests(d: DoorDraft): FlipRequest[] {
  return d.entries.filter(e => e.flipHome).map(e => ({
    turfMemberId: e.flipHome!.turfMemberId, turfId: d.turfId, contactId: e.contactId,
    afterClientVisitId: e.clientVisitId, movedFromLabel: e.flipHome!.movedFromLabel,
  }))
}

/** The Door results the resolved script allows (P3-6 effective set). No script: everything (server decides). */
export function allowedDoorResults(script: ResolvedScriptDto | null | undefined): Set<ResultStatus> {
  const door = script?.resultSettings?.find(s => s.contactMethod === ContactMethod.Door)
  return new Set(door ? door.allowedResults : Object.values(ResultStatus))
}

/** [P6-2] The organizer-governed follow-ups. System flags (NotAt*) are never governed. */
export const GOVERNED_FLAGS = DOOR_FOLLOW_UPS

/** [P6] The follow-ups the resolved script allows. Missing field (pre-Phase-6 cache) or no script: all of them. */
export function allowedDoorFlags(script: ResolvedScriptDto | null | undefined): Set<VisitActionFlag> {
  const door = script?.resultSettings?.find(s => s.contactMethod === ContactMethod.Door)
  return new Set(door?.allowedFlags ?? GOVERNED_FLAGS)
}

/** [P6] Drops switched-off follow-ups from an outcome; system flags always pass (mirrors the server, P6-1). */
export function withAllowedFlags(o: DraftOutcome, allowed: ReadonlySet<VisitActionFlag>): DraftOutcome {
  const flags = o.flags.filter(f => !GOVERNED_FLAGS.includes(f) || allowed.has(f))
  return flags.length === o.flags.length ? o : { ...o, flags }
}

/** Nobody home / Couldn't knock cover the residents not yet Done; on an all-Done door, everyone. */
export function peopleForDoorMiss(residents: TurfContactDto[]): TurfContactDto[] {
  const open = residents.filter(r => contactWalkState(r) !== 'done')
  return open.length > 0 ? open : residents
}

const draftKey = (turfId: string, stopKey: string) => `canvasser_door_${turfId}|${stopKey}`
// [P5-8] One saved conversation per door AND per set of people. The stop key is URI-encoded, so it can't
// contain '#', and the '#' terminator keeps door "A|1" from matching door "A|10" or "A|1#4".
const conversationPrefix = (turfId: string, stopKey: string) => `canvasser_script_${turfId}|${encodeURIComponent(stopKey)}#`
export const conversationKey = (turfId: string, stopKey: string, ids: string[]) =>
  `${conversationPrefix(turfId, stopKey)}${[...ids].sort().join(',')}`

export function saveDraft(d: DoorDraft): void {
  try { sessionStorage.setItem(draftKey(d.turfId, d.stopKey), JSON.stringify(d)) } catch { /* private mode */ }
}

export function loadDraft(turfId: string, stopKey: string): DoorDraft | null {
  try {
    const raw = sessionStorage.getItem(draftKey(turfId, stopKey))
    if (!raw) return null
    const d = JSON.parse(raw) as DoorDraft
    return typeof d?.doorVisitId === 'string' && Array.isArray(d.entries) ? d : null
  } catch {
    return null
  }
}

export function clearDraft(turfId: string, stopKey: string): void {
  try {
    sessionStorage.removeItem(draftKey(turfId, stopKey))
    sessionStorage.removeItem(`canvasser_script_${turfId}|${stopKey}`) // the Phase 4 per-door script state
    const prefix = conversationPrefix(turfId, stopKey)
    const doomed: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i)
      if (k?.startsWith(prefix)) doomed.push(k)
    }
    doomed.forEach(k => sessionStorage.removeItem(k))
  } catch { /* ignore */ }
}
